import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { RefreshCcw, Download, AlertTriangle, CheckCircle2, XCircle, Database } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { PostgresErrorViewer } from "./PostgresErrorViewer";
import { AppLogPanel } from "./AppLogPanel";
import { useDiagnosticLog } from "@/hooks/useDiagnosticLog";
import { EmailDiagnostics } from "@/components/diagnostics/EmailDiagnostics";
import { AuthDiagnostics } from "@/components/diagnostics/AuthDiagnostics";

export const DiagnosticsTab = () => {
  const [activeTab, setActiveTab] = useState('logs');
  const [supabseStatus, setSupabaseStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const { logs, clearLogs, queryVersion } = useDiagnosticLog();

  // Check Supabase connection
  useEffect(() => {
    const checkSupabase = async () => {
      try {
        // Check if we can connect to Supabase
        const { data, error } = await supabase.from('profiles').select('id').limit(1);
        
        if (error) {
          console.error('Supabase connection error:', error);
          setSupabaseStatus('error');
        } else {
          console.log('Supabase connection successful');
          setSupabaseStatus('success');
        }
      } catch (err) {
        console.error('Error checking Supabase:', err);
        setSupabaseStatus('error');
      }
    };
    
    checkSupabase();
  }, []);

  // Test and log logo URLs
  useEffect(() => {
    const testLogoUrls = async () => {
      const logoUrls = [
        'https://ginefbvgxcobyuocxerb.supabase.co/storage/v1/object/public/logos/fish-header.png',
        'https://ginefbvgxcobyuocxerb.supabase.co/storage/v1/object/public/logos/fish-footer.png'
      ];
      
      console.log('[Diagnostics] Testing logo URLs:', logoUrls);
      
      // Test each URL with a fetch request
      for (const url of logoUrls) {
        try {
          const response = await fetch(url, { method: 'HEAD' });
          if (response.ok) {
            console.log(`[Diagnostics] URL check success: ${url}`);
          } else {
            console.error(`[Diagnostics] URL check failed (${response.status}): ${url}`);
          }
        } catch (err) {
          console.error(`[Diagnostics] Error fetching URL: ${url}`, err);
        }
      }
      
      setImageUrls(logoUrls);
    };
    
    testLogoUrls();
  }, []);
  
  // Show Supabase storage buckets
  const [buckets, setBuckets] = useState<string[]>([]);
  
  useEffect(() => {
    const listBuckets = async () => {
      try {
        const { data, error } = await supabase.storage.listBuckets();
        
        if (error) {
          console.error('Error listing buckets:', error);
        } else {
          console.log('Available buckets:', data.map(b => b.name));
          setBuckets(data.map(b => b.name));
          
          // If logos bucket exists, list its files
          const logosBucket = data.find(b => b.name === 'logos');
          if (logosBucket) {
            const { data: files, error: filesError } = await supabase.storage.from('logos').list();
            
            if (filesError) {
              console.error('Error listing files in logos bucket:', filesError);
            } else {
              console.log('Files in logos bucket:', files);
            }
          }
        }
      } catch (err) {
        console.error('Error checking buckets:', err);
      }
    };
    
    listBuckets();
  }, []);

  // Export logs functionality
  const exportLogs = () => {
    try {
      const data = logs;
      const filename = `app-logs-${new Date().toISOString()}.json`;
      const contentType = "application/json";
      
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: contentType });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.download = filename;
      link.href = url;
      link.click();
      
      URL.revokeObjectURL(url);
      
      console.log('Logs exported successfully');
    } catch (error) {
      console.error('Error exporting logs:', error);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle>System Diagnostics</CardTitle>
          <Button 
            size="sm" 
            variant="outline" 
            onClick={() => window.location.reload()}
            className="flex items-center gap-1"
          >
            <RefreshCcw className="h-3 w-3" /> Refresh
          </Button>
        </div>
      </CardHeader>
      
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="logs" className="text-red-600">App Log</TabsTrigger>
            <TabsTrigger value="resources">Resources</TabsTrigger>
            <TabsTrigger value="network">Network</TabsTrigger>
            <TabsTrigger value="storage">Storage</TabsTrigger>
            <TabsTrigger value="database">
              <div className="flex items-center gap-1">
                <Database className="h-4 w-4" />
                <span>Database</span>
              </div>
            </TabsTrigger>
            <TabsTrigger value="email" className="text-red-600">Email Diagnostics</TabsTrigger>
            <TabsTrigger value="auth" className="text-red-600">Auth Diagnostics</TabsTrigger>
          </TabsList>
          
          <TabsContent value="logs">
            <div className="space-y-4">
              <div className="flex justify-end space-x-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={clearLogs}
                >
                  Clear Logs
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={exportLogs}
                >
                  <Download className="h-4 w-4 mr-1" /> Export
                </Button>
              </div>
              
              <AppLogPanel logs={logs} queryVersion={queryVersion} />
            </div>
          </TabsContent>
          
          <TabsContent value="resources">
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-2">Image Resources</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {imageUrls.map((url, i) => (
                    <Card key={i}>
                      <CardContent className="p-4">
                        <p className="text-sm font-mono mb-2 break-all">{url}</p>
                        <div className="border rounded bg-white p-2 max-w-xs mx-auto">
                          <img 
                            src={url} 
                            alt={`Test image ${i}`} 
                            className="max-h-32 object-contain mx-auto"
                            onLoad={() => console.log(`[Diagnostics] Image loaded: ${url}`)}
                            onError={() => console.error(`[Diagnostics] Image failed to load: ${url}`)}
                          />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="network">
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-2">Supabase Connection</h3>
                <div className="flex items-center gap-2">
                  {supabseStatus === 'loading' && (
                    <AlertTriangle className="h-5 w-5 text-blue-500" />
                  )}
                  {supabseStatus === 'success' && (
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                  )}
                  {supabseStatus === 'error' && (
                    <XCircle className="h-5 w-5 text-red-500" />
                  )}
                  
                  <span>
                    {supabseStatus === 'loading' ? 'Checking connection...' : 
                     supabseStatus === 'success' ? 'Connected to Supabase' : 
                     'Failed to connect to Supabase'}
                  </span>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold mb-2">Project Info</h3>
                <p className="text-sm">Supabase Project ID: ginefbvgxcobyuocxerb</p>
              </div>
              
              <Button 
                onClick={() => {
                  console.log('[Diagnostics] Testing Supabase connection...');
                  setSupabaseStatus('loading');
                  
                  (async () => {
                    try {
                      const { data, error } = await supabase.from('profiles')
                        .select('id')
                        .limit(1);
                      
                      if (error) {
                        console.error('[Diagnostics] Supabase connection test failed:', error);
                        setSupabaseStatus('error');
                      } else {
                        console.log('[Diagnostics] Supabase connection test succeeded');
                        setSupabaseStatus('success');
                      }
                    } catch (err) {
                      console.error('[Diagnostics] Unexpected error during Supabase test:', err);
                      setSupabaseStatus('error');
                    }
                  })();
                }}
                variant="outline"
                size="sm"
              >
                Test Connection
              </Button>
            </div>
          </TabsContent>
          
          <TabsContent value="storage">
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-2">Storage Buckets</h3>
                {buckets.length === 0 ? (
                  <div className="text-gray-400 p-4 text-center">No buckets found</div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {buckets.map((bucket, i) => (
                      <Card key={i}>
                        <CardContent className="p-4">
                          <h4 className="font-medium">{bucket}</h4>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="mt-2 text-xs"
                            onClick={() => {
                              console.log(`[Diagnostics] Listing files in bucket: ${bucket}`);
                              
                              (async () => {
                                try {
                                  const { data, error } = await supabase.storage
                                    .from(bucket)
                                    .list();
                                    
                                  if (error) {
                                    console.error(`[Diagnostics] Error listing files in ${bucket}:`, error);
                                  } else {
                                    console.log(`[Diagnostics] Files in ${bucket}:`, data);
                                  }
                                } catch (err) {
                                  console.error(`[Diagnostics] Unexpected error listing files in ${bucket}:`, err);
                                }
                              })();
                            }}
                          >
                            List Files
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
              
              <Separator className="my-4" />
              
              <div>
                <h3 className="text-lg font-semibold mb-2">Logo Image Tests</h3>
                <div className="flex flex-col gap-4">
                  <div className="p-4 border rounded">
                    <h4 className="font-medium mb-2">Header Logo Test</h4>
                    <div className="flex items-center gap-4">
                      <div className="bg-orange-50 p-2 w-20 h-20 flex items-center justify-center rounded">
                        <img 
                          src="https://ginefbvgxcobyuocxerb.supabase.co/storage/v1/object/public/logos/fish-header.png" 
                          alt="Header logo" 
                          className="max-h-16 max-w-16"
                          onLoad={() => console.log('[Diagnostics] Header logo loaded successfully')}
                          onError={() => console.error('[Diagnostics] Header logo failed to load')}
                        />
                      </div>
                      <div>
                        <p className="text-sm font-mono break-all">fish-header.png</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-4 border rounded">
                    <h4 className="font-medium mb-2">Footer Logo Test</h4>
                    <div className="flex items-center gap-4">
                      <div className="bg-white p-2 w-20 h-20 flex items-center justify-center rounded">
                        <img 
                          src="https://ginefbvgxcobyuocxerb.supabase.co/storage/v1/object/public/logos/fish-footer.png" 
                          alt="Footer logo" 
                          className="max-h-16 max-w-16"
                          onLoad={() => console.log('[Diagnostics] Footer logo loaded successfully')}
                          onError={() => console.error('[Diagnostics] Footer logo failed to load')}
                        />
                      </div>
                      <div>
                        <p className="text-sm font-mono break-all">fish-footer.png</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="database">
            <div className="space-y-6">
              <PostgresErrorViewer />
            </div>
          </TabsContent>
          
          <TabsContent value="email">
            <div className="space-y-6">
              <EmailDiagnostics />
            </div>
          </TabsContent>
          
          <TabsContent value="auth">
            <div className="space-y-6">
              <AuthDiagnostics />
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default DiagnosticsTab;
